import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  products, KINDLING,
  subscriptionMonthly, SUB_MIN_BUNDLES, SUB_MAX_BUNDLES, SUB_PER_BUNDLE, SUBSCRIPTIONS_ENABLED,
} from '../data/pricing';
import business from '../data/business';
import WoodTypeBadge from '../components/WoodTypeBadge';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function Pricing() {
  const [kindling, setKindling] = useState({ enabled: false, price: 0, quantity: 0 });
  useEffect(() => {
    axios.get(`${API_URL}/api/settings/availability`)
      .then((res) => { if (res.data.kindling) setKindling(res.data.kindling); })
      .catch(() => {});
  }, []);

  const displayProducts = kindling.enabled && kindling.quantity > 0
    ? [...products, { ...KINDLING, price: Number(kindling.price) || 0 }]
    : products;

  return (
    <div className="bg-cream">
      {/* Header */}
      <section className="bg-gradient-to-br from-walnut-700 via-walnut to-walnut-600 text-cream">
        <div className="mx-auto max-w-5xl px-4 py-16 text-center sm:px-6 sm:py-20 lg:px-8">
          <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl">Pricing</h1>
          <p className="mt-4 text-lg text-cream-300">
            Seasoned, ready-to-burn hardwood for
            {' '}
            {business.serviceArea}
            . Free delivery, right to your door.
          </p>
          <WoodTypeBadge className="mt-6" variant="light" />
        </div>
      </section>

      {/* Products */}
      <section className="mx-auto max-w-5xl px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <h2 className="text-center text-2xl font-extrabold tracking-tight text-walnut">Order anytime</h2>
        <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2">
          {displayProducts.map((p) => (
            <div key={p.id} className="flex items-start justify-between gap-4 rounded-2xl border border-cream-300 bg-white p-6 shadow-sm">
              <div className="min-w-0">
                <h3 className="text-lg font-bold text-walnut">
                  {p.name}
                  {p.addon && <span className="ml-2 rounded-full bg-walnut/10 px-2 py-0.5 text-[10px] font-semibold uppercase text-walnut-400">add-on</span>}
                </h3>
                <p className="mt-1 text-sm text-walnut-400">{p.description}</p>
              </div>
              <p className="shrink-0 text-2xl font-extrabold text-ember">{`$${p.price}`}</p>
            </div>
          ))}
        </div>
        <p className="mt-6 text-center text-sm text-walnut-300">
          Free delivery right to your door, here in
          {' '}
          {business.serviceArea}
          .
        </p>
      </section>

      {/* Subscriptions (hidden while SUBSCRIPTIONS_ENABLED is off) */}
      {SUBSCRIPTIONS_ENABLED && (
        <section className="bg-cream-300/40 py-16 sm:py-20">
          <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
            <h2 className="text-center text-2xl font-extrabold tracking-tight text-walnut">
              Monthly subscriptions
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-center text-sm text-walnut-300">
              {`Never run out — pick any size from ${SUB_MIN_BUNDLES} to ${SUB_MAX_BUNDLES} bundles, delivered every month at $${SUB_PER_BUNDLE} a bundle.`}
            </p>
            <p className="mx-auto mt-1 max-w-xl text-center text-xs text-walnut-300">
              Billed automatically to your card each month — cancel anytime.
            </p>
            <div className="mx-auto mt-10 grid max-w-3xl grid-cols-1 gap-6 sm:grid-cols-3">
              {[SUB_MIN_BUNDLES, 5, SUB_MAX_BUNDLES].map((n) => (
                <div key={n} className="rounded-2xl border border-cream-300 bg-white p-6 text-center shadow-sm">
                  <h3 className="text-lg font-bold text-walnut">{`${n} bundles / month`}</h3>
                  <p className="mt-2 text-3xl font-extrabold text-ember">{`$${subscriptionMonthly(n)}/mo`}</p>
                  <p className="mt-3 text-sm text-walnut-400">{`$${SUB_PER_BUNDLE} per bundle, delivered.`}</p>
                </div>
              ))}
            </div>
            <p className="mx-auto mt-6 max-w-xl text-center text-xs text-walnut-300">
              Want a different size? Choose anything in between on the order form.
            </p>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="mx-auto max-w-5xl px-4 py-16 text-center sm:px-6 lg:px-8">
        <Link
          to="/order"
          className="inline-block rounded-xl bg-ember px-8 py-3.5 text-base font-semibold text-white shadow-sm transition-colors hover:bg-ember-600"
        >
          Order now
        </Link>
        <p className="mt-3 text-sm text-walnut-300">Quick, secure checkout — pay by card or Venmo.</p>
      </section>
    </div>
  );
}

export default Pricing;
